import type { ExtendedMedicationRequest } from '@seen/core/models/medicationRequest'
import { MedicationRefillBlockNode as MedicationRefillBlockSchema } from '@seen/core/tiptap/schema'
import {
  type NodeViewProps,
  NodeViewWrapper,
  ReactNodeViewRenderer,
} from '@tiptap/react'
import type { JSX } from 'react'
import { useCallback, useContext, useMemo } from 'react'
import { MedicationRefillBlock } from '#src/components/rich-text-editor/MedicationRefillBlockNode/MedicationRefillBlock.tsx'
import { CompositionContext } from '#src/contexts/CompositionContext.tsx'
import { useElationDraftPrescription } from '#src/hooks/useElationDraftPrescription.ts'
import {
  combineDraftIdsByThread,
  medicationForDraftOpen,
  useMedicationDraftsByThread,
} from '#src/hooks/useMedicationDraftsByThread.ts'
import { useMedicationRefillVisitLines } from '#src/hooks/useMedicationRefillVisitLines.ts'

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    medicationRefillBlock: {
      insertMedicationRefillBlock: () => ReturnType
    }
  }
}

export const MedicationRefillBlockNode = MedicationRefillBlockSchema.extend({
  addNodeView() {
    return ReactNodeViewRenderer(MedicationRefillBlockNodeView)
  },

  addCommands() {
    return {
      insertMedicationRefillBlock:
        () =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
          })
        },
    }
  },
})

function MedicationRefillBlockNodeView(_props: NodeViewProps): JSX.Element {
  const compositionReferenceString = useContext(CompositionContext)
  const { meds, medicationById, isLoading, hasParticipant, participantId } =
    useMedicationRefillVisitLines(compositionReferenceString)
  const {
    createDraftPrescription,
    openDraftPrescription,
    draftingMedicationIds,
    optimisticDraftIdsByThreadId,
    canDraft,
  } = useElationDraftPrescription(participantId, {
    surface: 'visit_note_block',
  })
  const { draftByThreadId } = useMedicationDraftsByThread(
    participantId ? `Patient/${participantId}` : undefined
  )
  const openStampedDraft = useCallback(
    (draftId: number | string, medication?: ExtendedMedicationRequest) => {
      openDraftPrescription(
        draftId,
        medicationForDraftOpen({
          draftId,
          medication,
          draftsByThreadId: draftByThreadId,
        })
      )
    },
    [draftByThreadId, openDraftPrescription]
  )
  const draftIdsByThreadId = useMemo(
    () =>
      combineDraftIdsByThread({
        serverDraftsByThreadId: draftByThreadId,
        optimisticDraftIdsByThreadId,
      }),
    [draftByThreadId, optimisticDraftIdsByThreadId]
  )

  return (
    <NodeViewWrapper>
      <MedicationRefillBlock
        meds={meds}
        medicationById={medicationById}
        isLoading={isLoading}
        hasParticipant={hasParticipant}
        onDraftPrescription={canDraft ? createDraftPrescription : undefined}
        onOpenDraftPrescription={openStampedDraft}
        draftIdsByThreadId={draftIdsByThreadId}
        draftingMedicationIds={draftingMedicationIds}
      />
    </NodeViewWrapper>
  )
}
